import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AccessToken } from '@okta/okta-auth-js';
import { from, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import * as fromStore from '..';

/**
 * Allows access to the Okta migration page only for non-Okta (legacy) users
 * whose account is not a demo account. Both Okta users and demo accounts are
 * redirected to /dashboard.
 */
export const OktaMigrationGuard: CanActivateFn = (): Observable<boolean> => {
  const router = inject(Router);
  const oktaAuth = inject(OKTA_AUTH);
  const store = inject(Store<fromStore.State>);

  return from(oktaAuth.tokenManager.get('accessToken')).pipe(
    switchMap((token) => {
      const accessToken = token as AccessToken;
      if (accessToken?.accessToken) {
        router.navigate(['/dashboard']);
        return of(false);
      }

      return store.select(fromStore.getCurrentUserLoaded).pipe(
        tap((loaded) => {
          if (!loaded) {
            store.dispatch(new fromStore.LoadCurrentUser());
          }
        }),
        filter((loaded) => loaded),
        take(1),
        switchMap(() =>
          store.select(fromStore.getCurrentUser).pipe(
            take(1),
            map((user) => {
              const isOktaUser = (user?.OktaUsername__c?.length ?? 0) > 0;
              if (user?.Account?.Is_Demo_Account__c || isOktaUser) {
                router.navigate(['/dashboard']);
                return false;
              }
              return true;
            }),
          ),
        ),
      );
    }),
  );
};
